import { useRef, useState } from "react";

import { File } from "lucide-react";

import type { NotificationMailRequest } from "@/entities/notification/api";
import { useNotificationMail } from "@/entities/notification/api";

import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Input } from "@/shared/ui/input";
import { Modal } from "@/shared/ui/Modal";
import { Spinner } from "@/shared/ui/spinner";
import { Textarea } from "@/shared/ui/textarea";
import { Label } from "@/shared/ui/label";

export const SendNotificationCard = () => {
  const { mutateAsync, isPending } = useNotificationMail();
  const [destination, setDestination] = useState<"panel" | "tg">("panel");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleClick = async () => {
    const formData = new FormData();
    formData.append("destination", destination);
    formData.append("message", message);
    if (file) {
      formData.append("file", file);
    }

    // Преобразуем FormData в объект, совместимый с NotificationMailRequest
    const requestData: NotificationMailRequest = {
      destination: destination,
      message: message,
      file: file ? [URL.createObjectURL(file)] : [], // Преобразуем файл в URL или массив строк
    };

    await mutateAsync(requestData).then(() => setOpen(false));
  };

  const handleUploadFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <Modal.Trigger>Рассылка</Modal.Trigger>
      <Modal.Content>
        <Modal.Title>Рассылка</Modal.Title>
        <Textarea
          onChange={(e) => setMessage(e.target.value)}
          value={message}
          placeholder="Сообщение для рассылки"
          className="text-textColor"
        />
        <div className="flex w-full gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="h-10 flex-1 text-textColor">
                {destination === "panel" ? "В панель" : "В бота"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => setDestination("panel")}>
                В панель
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDestination("tg")}>
                В бота
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Label className="relative inline-flex cursor-pointer items-center">
            <Button
              className="h-full"
              onClick={handleUploadFile}
              disabled={destination === "panel"}
            >
              <File className="mr-2 h-5 w-5" />
              <span className="text-sm">Загрузить файл</span>

              <Input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                disabled={destination === "panel"}
                className="absolute inset-0 z-10 cursor-pointer !opacity-0"
              />
            </Button>
          </Label>
        </div>
        <div className="flex flex-col gap-2">
          <Button onClick={handleClick}>
            {isPending ? <Spinner /> : "Отправить"}
          </Button>

          <Modal.Close />
        </div>
      </Modal.Content>
    </Modal>
  );
};
